
import { getEvidenceDisplayName } from '@safetyhub/shared';

interface Contributor {
  id: string;
  userId: string;
  userName: string;
  /** 서명 완료 시점의 이름 스냅샷 (확정 우선) */
  signerName?: string;
  /** 참여 시점 스냅샷 이름 (서버 신설 필드) */
  joinSnapshotName?: string;
  role: 'PRIMARY' | 'SECONDARY';
  profileImageUrl?: string;
  completionStatus?: 'IN_PROGRESS' | 'COMPLETED';
  excludedAt?: string;
}

interface ContributorsBarProps {
  /** 점검자 이름 (주 담당자) */
  inspectorName: string;
  /** 기여자 목록 (시작자 포함 가능) */
  contributors?: Contributor[];
  /** 최대 표시할 기여자 수 */
  maxDisplay?: number;
  /** 클릭 시 상세 보기 */
  onClick?: () => void;
}

export function ContributorsBar({
  inspectorName,
  contributors = [],
  maxDisplay = 3,
  onClick,
}: ContributorsBarProps) {
  // 점검자를 제외한 추가 기여자 수
  const additionalCount = contributors.filter(p => p.role === 'SECONDARY').length;

  // 표시할 기여자 (프로필 이미지용)
  const displayContributors = contributors.slice(0, maxDisplay);
  const remainingCount = Math.max(0, contributors.length - maxDisplay);

  return (
    <div
      className={`flex items-center gap-2 text-sm text-ink-600 ${onClick ? 'cursor-pointer hover:text-ink-800' : ''}`}
      onClick={onClick}
    >
      {/* 기여자 아바타 스택 */}
      {displayContributors.length > 0 && (
        <div className="flex -space-x-2">
          {displayContributors.map((contributor, index) => (
            <div
              key={contributor.id}
              className="relative"
              style={{ zIndex: displayContributors.length - index }}
            >
              <div
                className={`w-7 h-7 rounded-full border-2 border-white flex items-center justify-center text-xs font-medium overflow-hidden ${
                  contributor.excludedAt
                    ? 'bg-ink-100 text-ink-400 line-through'
                    : 'bg-ink-200 text-ink-600'
                }`}
                title={`${getEvidenceDisplayName(contributor.signerName ?? contributor.joinSnapshotName, null, contributor.userName)}${contributor.completionStatus === 'COMPLETED' ? ' (완료)' : contributor.excludedAt ? ' (제외)' : ''}`}
              >
                {contributor.profileImageUrl ? (
                  <img
                    src={contributor.profileImageUrl}
                    alt={getEvidenceDisplayName(contributor.signerName ?? contributor.joinSnapshotName, null, contributor.userName)}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  getInitials(getEvidenceDisplayName(contributor.signerName ?? contributor.joinSnapshotName, null, contributor.userName))
                )}
              </div>
              {/* 완료 체크 표시 */}
              {contributor.completionStatus === 'COMPLETED' && !contributor.excludedAt && (
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full flex items-center justify-center border border-white">
                  <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </div>
          ))}
          {remainingCount > 0 && (
            <div
              className="relative w-7 h-7 rounded-full bg-ink-300 border-2 border-white flex items-center justify-center text-xs font-medium text-ink-600"
              style={{ zIndex: 0 }}
            >
              +{remainingCount}
            </div>
          )}
        </div>
      )}

      {/* 텍스트 정보 */}
      <span className="flex items-center gap-1">
        <span className="text-ink-500">주 담당자:</span>
        <span className="font-medium text-ink-800">{inspectorName}</span>
        {additionalCount > 0 && (
          <span className="text-ink-500">
            + {additionalCount}명 참여
          </span>
        )}
      </span>
    </div>
  );
}

/**
 * 이름에서 이니셜 추출
 */
function getInitials(name: string): string {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

/**
 * 간단한 기여자 수 표시 (텍스트만)
 */
export function ContributorCount({
  inspectorName,
  contributorCount,
}: {
  inspectorName: string;
  contributorCount: number;
}) {
  return (
    <span className="text-sm text-ink-600">
      <span className="text-ink-500">주 담당자:</span>
      <span className="ml-1 font-medium text-ink-800">{inspectorName}</span>
      {contributorCount > 0 && (
        <span className="ml-1 text-ink-500">+ {contributorCount}명</span>
      )}
    </span>
  );
}

export default ContributorsBar;
